(() => {
  'use strict';
  const GAP=3.175,EPS=0.00002,cache=new Map();
  const TUBES=[6,8,10,12],TUBE_PLUS=0.508,TUBE_MINUS=0.508;
  const escape=s=>String(s??'').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const t=s=>window.SVSelectorTranslations?.[s]||s;
  const number=n=>Number(n.toFixed(3)).toString();
  const size=n=>`${number(n)} mm / ${number(n/25.4)} in`;
  function drillTube(diameter){
    const supplied=TUBES.filter(n=>n>=diameter-TUBE_MINUS-EPS&&n<=diameter+TUBE_PLUS+EPS).sort((a,b)=>Math.abs(a-diameter)-Math.abs(b-diameter)||a-b);
    return {diameter:supplied[0]??diameter,included:supplied.length>0,minimum:Math.max(0,diameter-TUBE_MINUS),maximum:diameter+TUBE_PLUS,allowanceIn:0.02};
  }
  const span=cables=>cables.reduce((sum,c)=>sum+c.diameter,0)+Math.max(0,cables.length-1)*GAP;
  const row=cables=>{
    let x=-span(cables)/2;
    return cables.map((c,index)=>{const r=c.diameter/2;x+=r;const p={index,x,y:0,r};x+=r+GAP;return p;});
  };
  const validRound=(holes,radius)=>holes.every((h,i)=>Math.hypot(h.x,h.y)+h.r<=radius+EPS&&holes.slice(i+1).every(k=>Math.hypot(h.x-k.x,h.y-k.y)+EPS>=h.r+k.r+GAP));
  function centered(points,radius){
    const candidates=[{x:points.reduce((sum,h)=>sum+h.x,0)/points.length,y:points.reduce((sum,h)=>sum+h.y,0)/points.length},{x:0,y:0},...points.map(h=>({x:h.x,y:h.y}))];
    for(let i=0;i<points.length;i++)for(let j=i+1;j<points.length;j++){
      const a=points[i],b=points[j],ra=radius-a.r,rb=radius-b.r;
      const dx=b.x-a.x,dy=b.y-a.y,d=Math.hypot(dx,dy);
      if(d<EPS||d>ra+rb+EPS||d<Math.abs(ra-rb)-EPS)continue;
      const along=(ra*ra-rb*rb+d*d)/(2*d),side=Math.sqrt(Math.max(0,ra*ra-along*along));
      const x=a.x+along*dx/d,y=a.y+along*dy/d;
      candidates.push({x:x-side*dy/d,y:y+side*dx/d},{x:x+side*dy/d,y:y-side*dx/d});
    }
    for(const c of candidates){const holes=points.map(h=>({...h,x:h.x-c.x,y:h.y-c.y}));if(validRound(holes,radius))return holes;}
    return null;
  }
  function packRound(cables,radius){
    const sorted=cables.map((c,index)=>({index,r:c.diameter/2})).sort((a,b)=>b.r-a.r||a.index-b.index);
    const key=radius+':'+sorted.map(h=>h.r.toFixed(9)).join(',');
    if(cache.has(key)){const saved=cache.get(key);return saved?.map((h,i)=>({...h,index:sorted[i].index}))||null;}
    const remember=holes=>{if(cache.size>100)cache.clear();cache.set(key,holes?.map(h=>({...h})).sort((a,b)=>b.r-a.r||a.index-b.index)||null);return holes;};
    if(sorted.some(h=>h.r>radius+EPS))return remember(null);
    if(sorted.some((h,i)=>sorted.slice(i+1).some(k=>2*(h.r+k.r)+GAP>2*radius+EPS)))return remember(null);
    if(sorted.length===1)return remember([{...sorted[0],x:0,y:0}]);
    if(sorted.length===2){const holes=row(sorted.map(h=>({diameter:h.r*2}))).map((h,i)=>({...h,index:sorted[i].index}));return remember(validRound(holes,radius)?holes:null);}
    if(sorted.reduce((sum,h)=>sum+(h.r+GAP/2)**2,0)>(radius+GAP/2+EPS)**2)return remember(null);
    const n=sorted.length,seeds=[];
    if(n===3){
      const [a,b,c]=sorted,ab=a.r+b.r+GAP,ac=a.r+c.r+GAP,bc=b.r+c.r+GAP;
      const x=(ab*ab+ac*ac-bc*bc)/(2*ab),y=Math.sqrt(Math.max(0,ac*ac-x*x));
      seeds.push([{...a,x:0,y:0},{...b,x:ab,y:0},{...c,x,y}]);
    }
    const largest=sorted[0].r,pitch=largest*2+GAP;
    seeds.push(sorted.map((h,i)=>({...h,x:Math.cos(i*2*Math.PI/n-Math.PI/2)*pitch/(2*Math.sin(Math.PI/n)),y:Math.sin(i*2*Math.PI/n-Math.PI/2)*pitch/(2*Math.sin(Math.PI/n))})));
    if(n>=5){const ring=n-1,r=Math.max(pitch,pitch/(2*Math.sin(Math.PI/ring)));seeds.push(sorted.map((h,i)=>({...h,x:i?Math.cos((i-1)*2*Math.PI/ring)*r:0,y:i?Math.sin((i-1)*2*Math.PI/ring)*r:0})));}
    for(const seed of seeds){const holes=centered(seed,radius);if(holes)return remember(holes);}
    const straight=row(sorted.map(h=>({diameter:h.r*2}))).map((h,i)=>({...h,index:sorted[i].index}));
    if(validRound(straight,radius))return remember(straight);
    let randomState=19790317;
    const random=()=>{randomState=(Math.imul(randomState,1664525)+1013904223)>>>0;return randomState/4294967296;};
    for(let attempt=0;attempt<12;attempt++){
      const points=sorted.map((h,i)=>{
        const angle=attempt<4?i*2.399963229728653+attempt*Math.PI/8:random()*2*Math.PI;
        const length=(radius-h.r)*(attempt<4?Math.sqrt((i+0.5)/n):Math.sqrt(random()));
        return {...h,x:Math.cos(angle)*length,y:Math.sin(angle)*length};
      });
      for(let step=0;step<160;step++){
        let violation=0;
        for(let a=0;a<n;a++)for(let b=a+1;b<n;b++){
          const i=(a+step)%n,j=(b+step)%n,p=points[i],q=points[j];
          let dx=q.x-p.x,dy=q.y-p.y,d=Math.hypot(dx,dy);
          const needed=p.r+q.r+GAP+EPS/4,overlap=needed-d;
          if(overlap<=0)continue;
          violation=Math.max(violation,overlap);
          if(d<1e-10){dx=1;dy=0;d=1;}
          const shift=overlap/2;p.x-=dx/d*shift;p.y-=dy/d*shift;q.x+=dx/d*shift;q.y+=dy/d*shift;
        }
        for(const p of points){const length=Math.hypot(p.x,p.y),limit=radius-p.r;if(length>limit&&length>0){violation=Math.max(violation,length-limit);p.x*=limit/length;p.y*=limit/length;}}
        if(violation<EPS/2&&validRound(points,radius))return remember(points.map(p=>({...p})));
      }
    }
    return remember(null);
  }
  function check(p,cables){
    const n=cables.length;
    if(!n||n>12||cables.some(c=>!Number.isFinite(c.diameter)||c.diameter<=0||c.diameter>p.cableMax+EPS))return {fits:false,span:span(cables),method:'invalid',holes:[]};
    const tubes=cables.map(c=>drillTube(c.diameter)),planned=cables.map((c,i)=>({...c,diameter:Math.max(c.diameter,tubes[i].diameter)})),total=span(planned);
    const source=p.layout;
    if(!source)return {fits:false,span:total,method:'unverified',holes:[]};
    let holes,shape,method,limit,schematic=false;
    if(source.type==='marked-centers'){
      const pattern=n===1?{maxEach:p.cableMax,maxSpan:p.cableMax}:source.patterns[String(n)];
      if(!pattern||cables.some(c=>c.diameter>pattern.maxEach+EPS)||total>pattern.maxSpan+EPS)return {fits:false,span:total,limit:pattern?.maxSpan,method:'marked-centers',holes:[]};
      if(planned.some(c=>c.diameter>pattern.maxEach+EPS))return {fits:false,span:total,limit:pattern.maxSpan,method:'marked-centers',holes:[]};
      holes=row(planned);limit=pattern.maxSpan;shape={type:'capsule',width:limit,height:pattern.maxEach};method='marked-centers';schematic=true;
    }else if(source.type==='slot'){
      limit=source.usableSpan;
      if(total>limit+EPS||planned.some(c=>c.diameter>p.cableMax+EPS))return {fits:false,span:total,limit,method:'slot',holes:[]};
      holes=row(planned);shape={type:'capsule',width:limit,height:p.cableMax};method='slot';
    }else{
      limit=source.usableSpan;shape={type:'circle',width:limit,height:limit};
      if(p.direction==='horizontal'){holes=total<=limit+EPS?row(planned):null;method='horizontal-row';}
      else{holes=packRound(planned,limit/2);method='nested-round';}
      if(!holes)return {fits:false,span:total,limit,method,holes:[],reason:'no_verified_layout'};
    }
    holes.sort((a,b)=>a.index-b.index);
    const edgeClearance=Math.min(...holes.map(h=>{
      if(shape.type==='circle')return shape.width/2+GAP-Math.hypot(h.x,h.y)-h.r;
      const halfSegment=(shape.width-shape.height)/2,nearest=Math.max(-halfSegment,Math.min(halfSegment,h.x));
      return shape.height/2+GAP-Math.hypot(h.x-nearest,h.y)-h.r;
    }));
    let holeClearance=null;
    for(let i=0;i<n;i++)for(let j=i+1;j<n;j++){const a=holes[i],b=holes[j],gap=Math.hypot(a.x-b.x,a.y-b.y)-a.r-b.r;holeClearance=holeClearance===null?gap:Math.min(holeClearance,gap);}
    return {fits:true,span:total,limit,method,holes,shape,edgeMargin:GAP,edgeClearance,holeClearance,schematic,tubes};
  }
  function razorCuts(layout){
    if(!layout.fits||!layout.shape)return [];
    const {shape,holes}=layout,R=shape.height/2+GAP,half=(shape.width-shape.height)/2;
    const boundary=(h,ux,uy)=>{
      const hits=[];
      const circle=(cx,side)=>{
        const dx=h.x-cx,dot=dx*ux+h.y*uy,disc=dot*dot+R*R-dx*dx-h.y*h.y;
        if(disc<0)return;
        const distance=-dot+Math.sqrt(disc),px=h.x+ux*distance;
        if(distance>0&&(!side||side*px>=half-EPS))hits.push(distance);
      };
      if(shape.type==='circle')circle(0,0);
      else{
        if(Math.abs(uy)>EPS)for(const edge of [-R,R]){const distance=(edge-h.y)/uy;if(distance>0&&Math.abs(h.x+ux*distance)<=half+EPS)hits.push(distance);}
        circle(-half,-1);circle(half,1);
      }
      return Math.min(...hits);
    };
    return holes.map(h=>{
      const radius=layout.tubes[h.index].diameter/2,candidates=[];
      for(let i=0;i<720;i++){
        const angle=i*Math.PI/360,ux=Math.cos(angle),uy=Math.sin(angle),distance=boundary(h,ux,uy);
        if(!Number.isFinite(distance)||distance<=radius)continue;
        const start={x:h.x+ux*radius,y:h.y+uy*radius},end={x:h.x+ux*distance,y:h.y+uy*distance};
        const clear=holes.every(k=>{
          if(k.index===h.index)return true;
          const along=Math.max(radius,Math.min(distance,(k.x-h.x)*ux+(k.y-h.y)*uy));
          return Math.hypot(k.x-h.x-ux*along,k.y-h.y-uy*along)>k.r+EPS;
        });
        if(clear)candidates.push({index:h.index,start,end,length:distance-radius});
      }
      candidates.sort((a,b)=>a.length-b.length);
      return candidates[0]||null;
    }).filter(Boolean);
  }
  function svg(p,layout,cables,idSuffix=''){
    if(!layout.fits||!layout.shape)return '';
    const {shape,holes}=layout,outerW=shape.width+GAP*2,outerH=shape.height+GAP*2;
    const scale=Math.min(280/outerW,194/outerH),cx=180,top=52,cy=top+outerH*scale/2;
    const x=n=>cx+n*scale,y=n=>cy+n*scale;
    const bottom=top+outerH*scale,legendY=bottom+57,legendEnd=legendY+cables.length*49,height=legendEnd+(layout.schematic?194:164);
    const id='sv-layout-'+p.sku.toLowerCase()+idSuffix,colors=['#006b94','#aa384b','#56762f','#75549b','#996400','#008177'];
    const text=(px,py,content,extra='')=>`<text x="${number(px)}" y="${number(py)}" ${extra.includes('font-size=')?'':'font-size="12"'} ${extra.includes('fill=')?'':'fill="#173446"'} ${extra}>${escape(content)}</text>`;
    const outline=(width,height,fill,stroke,extra='')=>shape.type==='circle'?`<circle cx="${cx}" cy="${number(cy)}" r="${number(width*scale/2)}" fill="${fill}" stroke="${stroke}" ${extra}/>`:`<rect x="${number(x(-width/2))}" y="${number(y(-height/2))}" width="${number(width*scale)}" height="${number(height*scale)}" rx="${number(height*scale/2)}" fill="${fill}" stroke="${stroke}" ${extra}/>`;
    const arrow=(ax,ay,bx,by,color='#173446')=>{
      const length=Math.hypot(bx-ax,by-ay),ux=(bx-ax)/(length||1),uy=(by-ay)/(length||1),a=Math.min(4,length/3),w=a*0.6;
      const triangle=(px,py,direction)=>`${number(px)},${number(py)} ${number(px+direction*ux*a-uy*w)},${number(py+direction*uy*a+ux*w)} ${number(px+direction*ux*a+uy*w)},${number(py+direction*uy*a-ux*w)}`;
      return `<g stroke="${color}" stroke-width="1"><path d="M${number(ax)} ${number(ay)}L${number(bx)} ${number(by)}"/><polygon points="${triangle(ax,ay,1)}" fill="${color}"/><polygon points="${triangle(bx,by,-1)}" fill="${color}"/></g>`;
    };
    let content=`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 ${number(height)}" role="img" aria-labelledby="${id}-title ${id}-desc" class="svs-layout-svg" style="font-family:Arial,sans-serif"><title id="${id}-title">${escape(p.sku+' '+t('top-down cable layout'))}</title><desc id="${id}-desc">${escape(t('Entered cable diameters are drawn to scale. Keep at least 1/8 inch of rubber at the edge and between holes.')+' '+cables.map((c,i)=>t('Cable')+' '+(i+1)+': '+size(c.diameter)).join('; '))}</desc>`;
    content+=text(180,19,p.sku+' · '+t('Top-down cable layout'),'text-anchor="middle" font-weight="700"');
    content+=outline(outerW,outerH,'#f5e8db','#947a65','stroke-width="1.3"');
    content+=outline(shape.width,shape.height,'#f6f8fa','#738494','stroke-width="1.2" stroke-dasharray="4 3"');
    content+=arrow(cx,top,cx,top+GAP*scale,'#b93636');
    content+=`<path d="M${cx+5} ${number(top+GAP*scale/2)}L${cx+26} 35" stroke="#b93636" fill="none"/>`;
    content+=text(cx+30,37,'1/8″ (3.175 mm)','fill="#b93636"');
    for(const h of holes){
      const px=x(h.x),py=y(h.y),r=cables[h.index].diameter*scale/2,tube=layout.tubes[h.index],color=colors[h.index%colors.length];
      content+=`<circle cx="${number(px)}" cy="${number(py)}" r="${number(r)}" fill="white" stroke="${color}" stroke-width="1.7"/>`;
      if(Math.abs(tube.diameter-cables[h.index].diameter)>EPS)content+=`<circle cx="${number(px)}" cy="${number(py)}" r="${number(tube.diameter*scale/2)}" fill="none" stroke="${color}" stroke-width="1" stroke-dasharray="3 2"/>`;
      content+=arrow(px-r,py,px+r,py,color);
      if(r>=7){content+=`<circle cx="${number(px)}" cy="${number(py)}" r="${number(Math.min(8,r-2))}" fill="white"/>`;content+=text(px,py+4,String(h.index+1),`text-anchor="middle" font-weight="700" font-size="${number(Math.min(12,r*1.5))}" fill="${color}"`);}
      else{content+=`<path d="M${number(px)} ${number(py-r)}v-6" stroke="${color}"/>`;content+=text(px,py-r-8,String(h.index+1),`text-anchor="middle" font-size="10" fill="${color}"`);}
    }
    for(const cut of razorCuts(layout))content+=`<line class="svs-razor-cut" data-hole="${cut.index+1}" x1="${number(x(cut.start.x))}" y1="${number(y(cut.start.y))}" x2="${number(x(cut.end.x))}" y2="${number(y(cut.end.y))}" stroke="#b76800" stroke-width="1.5" stroke-dasharray="6 3"/>`;
    if(holes.length>1){
      let closest=null;
      for(let i=0;i<holes.length;i++)for(let j=i+1;j<holes.length;j++){
        const a=holes[i],b=holes[j],distance=Math.hypot(b.x-a.x,b.y-a.y),gap=distance-a.r-b.r;
        if(!closest||gap<closest.gap)closest={a,b,distance,gap};
      }
      const {a,b,distance}=closest,ux=(b.x-a.x)/distance,uy=(b.y-a.y)/distance;
      content+=arrow(x(a.x+ux*a.r),y(a.y+uy*a.r),x(b.x-ux*b.r),y(b.y-uy*b.r),'#b93636');
    }
    const dimensionY=bottom+24;
    content+=`<path d="M${number(x(-shape.width/2))} ${number(cy)}V${number(dimensionY+4)}M${number(x(shape.width/2))} ${number(cy)}V${number(dimensionY+4)}" stroke="#a6b2bb" stroke-width="0.8" fill="none"/>`;
    content+=arrow(x(-shape.width/2),dimensionY,x(shape.width/2),dimensionY);
    content+=text(cx,dimensionY+17,t('Drilling envelope')+': '+size(shape.width),'text-anchor="middle"');
    holes.forEach((h,i)=>{
      const lineY=legendY+i*49,color=colors[h.index%colors.length],tube=layout.tubes[h.index];
      content+=`<circle cx="30" cy="${number(lineY-4)}" r="8" fill="${color}"/>`;
      content+=`<text x="30" y="${number(lineY)}" font-size="11" fill="white" text-anchor="middle">${h.index+1}</text>`;
      content+=text(46,lineY,t('Cable')+' '+(h.index+1)+' · Ø '+size(cables[h.index].diameter));
      content+=text(46,lineY+17,t('Suggested tube')+': Ø '+size(tube.diameter),'font-size="11"');
      content+=text(46,lineY+31,t(tube.included?'Supplied tube':'Matching tube needed; not supplied'),'font-size="10"');
    });
    content+=`<path d="M22 ${number(legendEnd-5)}H338" stroke="#d6e1e8"/>`;
    content+=text(22,legendEnd+15,t('Edge rubber')+': ≥ 1/8″ (3.175 mm)');
    if(holes.length>1)content+=text(22,legendEnd+34,t('Between holes')+': ≥ 1/8″ (3.175 mm)','fill="#b93636"');
    content+=text(22,legendEnd+54,t('Shaded band: minimum required edge rubber.'));
    content+=text(22,legendEnd+71,t('Conservative outline; actual plug size may be larger.'));
    if(layout.schematic){content+=text(22,legendEnd+90,t('CGM90: use the factory-marked drill centers.'));content+=text(22,legendEnd+107,t('This view does not dimension their exact positions.'));}
    content+=text(22,legendEnd+(layout.schematic?125:90),t('Tube tolerance: ±0.02″ from cable.'));
    content+=text(22,legendEnd+(layout.schematic?143:108),t('Solid: cable. Fine dashes: suggested hole.'));
    content+=`<line x1="22" y1="${number(legendEnd+(layout.schematic?156:121))}" x2="42" y2="${number(legendEnd+(layout.schematic?156:121))}" stroke="#b76800" stroke-width="1.5" stroke-dasharray="6 3"/>`;
    content+=text(48,legendEnd+(layout.schematic?160:125),t('Razor cut: hole edge to rubber edge.'),'font-size="11"');
    content+=text(22,legendEnd+(layout.schematic?178:143),t('Planning view; not a drilling template.'));
    content+='</svg>';
    return content;
  }
  function card(p,layout,cables){
    if(!layout.fits)return '';
    return `<details class="svs-layout" open><summary>${escape(t('View your cable layout'))}<span aria-hidden="true">⌄</span></summary><div class="svs-layout-body">${svg(p,layout,cables)}<p>${escape(t('Planning view, not a drilling template. Verify the actual plug and installation instructions before drilling.'))}</p><div class="svs-layout-actions"><button type="button" class="svs-layout-download" data-layout-view="${escape(p.sku)}" aria-haspopup="dialog">${escape(t('Enlarge drawing'))}</button><button type="button" class="svs-layout-download" data-layout-download="${escape(p.sku)}">${escape(t('Download dimensional drawing'))}</button></div></div></details>`;
  }
  window.SVCableLayout={check,span,svg,card,drillTube,razorCuts,GAP};
})();
